import axios from 'axios';
import { authHeaders as sharedAuthHeaders } from './apiHeaders';
import { getActiveTenantId } from './tenantContext';
import { attachAuthRefresh } from './apiClient';
import {
  DashboardResponse,
  DashboardQueryParams,
  ExecutiveDashboard,
  TimePeriod,
  ProviderSummaryData,
} from '../types/dashboard';

/**
 * Dashboard API service
 * Handles all dashboard data fetching
 * Maps to Requirement 76 AC 3: P95 ≤ 2 seconds for 90-day scope
 */

const API_BASE_URL = import.meta.env.VITE_COST_API_URL
  ? `${import.meta.env.VITE_COST_API_URL}/api/v1`
  : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:3002/api/v1');

// Every raw axios.get() call in this file needs this - the backend's
// tenantContextMiddleware requires a verified Bearer token, so tenant
// headers alone are no longer enough to get past it.
// Delegates to the shared builder so a user Refresh (withFreshData) adds
// Cache-Control: no-cache here too. Ten local copies of this function is why
// the Refresh button worked on some pages and silently did nothing on others.
function authHeaders(): Record<string, string> {
  return sharedAuthHeaders();
}

/**
 * Expand any payer/management account in a budgeted set to include its members.
 *
 * A consolidated AWS CUR attributes every line to the MEMBER account that
 * incurred it, never to the payer. So a budget set on the payer - the account
 * you would naturally budget at, and the one the picker marks 'master' -
 * matches nothing in the cost breakdown, and the pace calculation would either
 * read $0 of spend or fall back to comparing against the whole tenant.
 *
 * Members are identified as 'member' accounts of the same provider on the
 * tenant. cloud_accounts has no column tying a member to a specific payer, so
 * that is the available definition; it is correct for one organisation per
 * cloud, which is what the onboarding flow creates.
 *
 * Mirrors accountIdsCoveredByBudget() in the backend's budget-alert.service -
 * the dashboard tile and the alert that fires on the same budget must agree on
 * what the budget covers, or the tile says "on pace" while an alert says 110%.
 */
async function expandPayerAccounts(budgetedIds: Set<string>): Promise<Set<string>> {
  if (budgetedIds.size === 0) return budgetedIds;

  try {
    const res = await axios.get(`${API_BASE_URL}/config/budget-scopes`, {
      headers: authHeaders(),
      timeout: 10000,
    });
    const accounts: any[] = res.data?.data || [];
    if (accounts.length === 0) return budgetedIds;

    const expanded = new Set(budgetedIds);
    for (const account of accounts) {
      const id = String(account.accountId || '').toLowerCase();
      if (!id || account.accountType !== 'master' || !budgetedIds.has(id)) continue;

      for (const member of accounts) {
        if (member.accountType === 'member' && member.provider === account.provider) {
          expanded.add(String(member.accountId || '').toLowerCase());
        }
      }
      console.log('ℹ️ Budget set on a payer account - rolling up its members', {
        payer: id,
        provider: account.provider,
      });
    }
    return expanded;
  } catch (e: any) {
    // No rollup is the previous behaviour, and it fails loudly further down
    // (the "do not match any account" warning) rather than quietly wrong.
    console.warn('⚠️ Could not expand payer accounts for budget coverage:', e.message);
    return budgetedIds;
  }
}

class DashboardService {
  private axiosInstance = attachAuthRefresh(axios.create({
    baseURL: API_BASE_URL,
    timeout: 90000, // 90 seconds for Athena queries
    headers: {
      'Content-Type': 'application/json',
    },
  }));

  constructor() {
    // Add auth token interceptor
    this.axiosInstance.interceptors.request.use((config) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // Add tenant context headers for local MVP
      config.headers['x-tenant-id'] = getActiveTenantId();
      config.headers['x-user-id'] = 'user-123';
      return config;
    });
    // Note: this instance is already wrapped in attachAuthRefresh above,
    // which silently refreshes and retries on a 401 before ever redirecting -
    // a second, redundant "401 -> straight to /login" interceptor used to
    // live here too, registered after attachAuthRefresh's, so it only fired
    // when the shared refresh itself failed (which, before today's
    // single-flight fix, happened often due to a refresh-token race). Removed
    // as dead weight now that the real fix is in authService.refreshToken().
  }

  /**
   * Fetch executive dashboard data
   * AC 1: Provide executive dashboard with all required metrics
   * AC 3: Query latency P95 ≤ 2 seconds for 90-day scope
   */
  async getDashboard(params: DashboardQueryParams): Promise<ExecutiveDashboard> {
    try {
      const response = await this.axiosInstance.get<DashboardResponse>('/dashboard/executive', {
        params: {
          timePeriod: params.timePeriod,
          view: params.view,
          startDate: params.startDate,
          endDate: params.endDate,
          ...(params.provider && { provider: params.provider }),
        },
      });

      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      throw error;
    }
  }

  /**
   * Get dashboard data with real cost from Cost API
   * Reads directly from S3 via Athena (no mock fallback)
   * 
   * RESILIENT DESIGN: Individual API failures don't crash the entire dashboard.
   * - Budget, forecast, optimization data: gracefully degrade to "not available" states
   * - Core cost data: must succeed or dashboard shows error
   */
  async getMockDashboard(params: DashboardQueryParams): Promise<ExecutiveDashboard> {
    // Fetch real cost data from Cost API (which reads from S3)
    const { startDate, endDate } = this.getDateRangeForParams(params.timePeriod);
    // Convert dates to ISO format with time
    const startISO = `${startDate}T00:00:00Z`;
    const endISO = `${endDate}T23:59:59Z`;
    // REQ-012: Provider filter parameter - when specified, all API calls filter to this provider
    const provider = params.provider;
    
    console.log('📊 Fetching real cost data from Cost API (S3)...', { startISO, endISO, provider });
    
    try {
      // First, fetch budget to pass to monthly trend data.
      //
      // A budget belongs to ONE period (a calendar month) and ONE scope. It is
      // always fetched for the CURRENT month, independently of which viewing
      // window the user picked - a budget is a monthly commitment, not a
      // property of the chart's date range.
      const budgetPeriod = new Date().toISOString().slice(0, 7); // YYYY-MM
      let budget: number | null = null;
      let budgetConfigured = false;
      let budgetCurrency: string = 'USD';
      /**
       * The account ids the summed budget above actually covers.
       *
       * Needed because the budget is now a SUM of per-account budgets, and a
       * tenant is rarely 100% budgeted. Comparing "the budget of the 2 accounts
       * that have one" against "the spend of all 9 accounts" is an
       * apples-to-oranges comparison that produced a confirmed-live pace of
       * 2204% over. Spend is narrowed to these accounts before the comparison.
       *
       * Lower-cased because Azure subscription GUIDs are case-insensitive and
       * do not always arrive in the same case from the cost export as from the
       * cloud-accounts table.
       */
      let budgetedAccountIds = new Set<string>();
      /**
       * Every month's budget total, keyed 'YYYY-MM'.
       *
       * The trend chart draws a budget line across whatever window is selected,
       * and it used to draw THIS month's budget flat across all of it. On "Last
       * 12 Months" that asserts every one of those months had the budget you
       * set most recently: a January bar of $12,000 sat under a $30,000 line
       * and looked healthy when January's actual budget was $10,000 and it was
       * $2,000 over. Each bucket now gets its own month's budget, so the line
       * is a step rather than a flat claim about the past.
       */
      const budgetByPeriod: Record<string, number> = {};
      try {
        const budgetRes = await axios.get(`${API_BASE_URL}/config/budgets`, {
          // NO `period` filter: every month is fetched in one request, because
          // the chart needs the whole history, not just this month. Grouping
          // below is what keeps the periods apart - an earlier version pinned
          // `period` precisely because the ungrouped code summed every month
          // together into one number.
          params: {},
          headers: authHeaders(),
          timeout: 8000,
        });

        const responseData = budgetRes.data?.data;
        budgetConfigured = responseData?.configured === true;

        // PROVIDER FILTER IS CLIENT-SIDE, deliberately.
        //
        // This used to pass `provider` as a query parameter and a comment here
        // claimed the server applied it. It does not: GET /config/budgets reads
        // only `period` and `scope_type` (see config.routes.ts), so the
        // parameter was silently discarded and a dashboard filtered to AWS
        // summed the Azure and GCP budgets too - a budget line far too high on
        // every single-provider view. Account budgets carry scope.provider, so
        // the filter belongs here where it can actually be applied.
        const allBudgets: any[] = responseData?.budgets || [];
        const budgets = provider
          ? allBudgets.filter(
              (b: any) =>
                b.scope?.provider === provider ||
                // Legacy tenant-wide rows have no provider and apply to
                // everything, so they are not excluded by a provider filter.
                (b.scope?.type ?? 'all') === 'all' ||
                // An account budget written before scope.provider existed. We
                // cannot tell which cloud it belongs to, and dropping it would
                // make a real budget vanish from a filtered view with no
                // explanation. Keeping it can only overstate the budget, which
                // is the visible failure rather than the silent one.
                (b.scope?.type === 'account' && !b.scope?.provider)
            )
          : allBudgets;

        // Group by month first, so each period's total is independent.
        const byPeriod = new Map<string, { accounts: any[]; legacyAll: any | null }>();
        for (const b of budgets) {
          const p = String(b.period || '');
          if (!p) continue;
          const entry = byPeriod.get(p) || { accounts: [], legacyAll: null };
          if (b.scope?.type === 'account') entry.accounts.push(b);
          else if ((b.scope?.type ?? 'all') === 'all' && !entry.legacyAll) entry.legacyAll = b;
          byPeriod.set(p, entry);
        }
        for (const [p, entry] of byPeriod.entries()) {
          const total =
            entry.accounts.length > 0
              ? entry.accounts.reduce((s: number, b: any) => s + (parseFloat(b.amount) || 0), 0)
              : entry.legacyAll
              ? parseFloat(entry.legacyAll.amount) || 0
              : 0;
          if (total > 0) budgetByPeriod[p] = total;
        }

        // The tile below still concerns THIS month only - a budget is a monthly
        // commitment, not a property of the chart's date range.
        const currentPeriod = byPeriod.get(budgetPeriod);
        const budgetsThisPeriod: any[] = currentPeriod
          ? [...currentPeriod.accounts, ...(currentPeriod.legacyAll ? [currentPeriod.legacyAll] : [])]
          : [];

        // SUM the budgets in view — because budgets are now only ever set per
        // ACCOUNT, and provider/tenant totals are DERIVED sums of those.
        //
        // This used to SELECT a single applicable budget and deliberately
        // refuse to sum, for a reason that was correct at the time: a budget
        // scoped to one account was a SUBSET of a tenant-wide 'all' budget,
        // so "All accounts: $10k" plus "Account X: $2k" still meant a $10k
        // ceiling, and adding them produced the confirmed-live symptom of a
        // $10k budget displaying as $12k.
        //
        // That overlap no longer exists. Budget Settings writes account-scoped
        // budgets only; there is no 'all' budget for an account budget to be a
        // subset of, so the sum of the accounts in view IS the budget for that
        // view. Any legacy 'all' row is therefore ignored rather than added,
        // which would reintroduce exactly the double-count described above.
        //
        // Scoped to the current period only - see budgetsThisPeriod above.
        const accountBudgets = budgetsThisPeriod.filter((b: any) => b.scope?.type === 'account');
        const legacyAll = budgetsThisPeriod.find((b: any) => (b.scope?.type ?? 'all') === 'all');

        if (accountBudgets.length > 0) {
          // PostgreSQL DECIMAL comes back as a string, so parseFloat is required.
          budget = accountBudgets.reduce(
            (sum: number, b: any) => sum + (parseFloat(b.amount) || 0),
            0
          );
          budgetCurrency = accountBudgets[0].currency || 'USD';
          budgetConfigured = budget > 0;
          budgetedAccountIds = await expandPayerAccounts(
            new Set(
              accountBudgets
                .map((b: any) => String(b.scope?.value || '').toLowerCase())
                .filter(Boolean)
            )
          );
          console.log('✅ Budget loaded (sum of account budgets in view)', {
            period: budgetPeriod,
            provider: provider || 'all',
            accounts: accountBudgets.length,
            amount: budget,
            currency: budgetCurrency,
          });
        } else if (legacyAll) {
          // A tenant-wide budget predating the per-account model. Honoured so
          // existing tenants do not lose their line overnight, but never added
          // to account budgets.
          budget = parseFloat(legacyAll.amount) || 0;
          budgetCurrency = legacyAll.currency || 'USD';
          budgetConfigured = budget > 0;
          console.log('✅ Budget loaded (legacy tenant-wide budget)', {
            period: budgetPeriod,
            amount: budget,
          });
        } else {
          budgetConfigured = false;
          console.log('ℹ️ No budget configured for', budgetPeriod, '- prompting to set one');
        }
      } catch (e: any) {
        console.warn('⚠️ Failed to fetch budget config:', e.message);
        // No fallback - budget remains null
      }

      // Fetch cost summary and monthly trend data in PARALLEL for faster loading
      const [costResponse, monthlyData] = await Promise.all([
        axios.get(`${API_BASE_URL}/cost/summary`, {
          params: { start: startISO, end: endISO, ...(provider && { provider }) },
          headers: authHeaders(),
          timeout: 90000,
        }),
        this.fetchMonthlyTrendData(startISO, endISO, params.timePeriod, budget, provider, budgetByPeriod),
      ]);
      
      if (!costResponse.data || !costResponse.data.data) {
        throw new Error('Invalid response from Cost API');
      }

      // Non-fatal problems the API reports ALONGSIDE the data.
      //
      // A provider returning zero can mean "nothing onboarded" or "we could
      // not read the credentials", and until the API started saying which,
      // both rendered as $0.00 with nothing to explain it. On 2026-10-07 an
      // Azure permission failure showed as Azure $0.00 for hours while the
      // real cause (a 403 reading the account credential from Key Vault) was
      // only visible in container logs that the corporate proxy blocks.
      //
      // The envelope's `errors` array was always there; nothing in the UI read
      // it. Surfaced on the Dashboard so a broken provider says so.
      const dataWarnings: Array<{ code: string; message: string }> =
        Array.isArray(costResponse.data.errors) ? costResponse.data.errors : [];

      const totalCost = costResponse.data.data.total_cost;
      const currency = costResponse.data.data.currency || 'USD';
      const breakdown = costResponse.data.data.breakdown || {};
      
      console.log('✅ Using REAL cost data from S3:', totalCost);

      // Extract region breakdown
      const byRegion = breakdown.by_region || {};
      const regionData = Object.entries(byRegion).map(([region, cost]) => ({
        region,
        cost: cost as number,
        percentage: ((cost as number) / totalCost) * 100,
      }));

      // Extract service breakdown
      const byService = breakdown.by_service || {};
      const serviceData = Object.entries(byService).map(([service, cost]) => ({
        service,
        cost: cost as number,
        percentage: ((cost as number) / totalCost) * 100,
      }));

      // Extract account breakdown
      const byAccount = breakdown.by_account || {};
      const accountData = Object.entries(byAccount).map(([account, cost]) => ({
        account,
        cost: cost as number,
        percentage: ((cost as number) / totalCost) * 100,
      }));

      // Extract provider breakdown (Phase 3: Multi-cloud support)
      const byProvider = breakdown.by_provider || {};
      const providerData = Object.entries(byProvider).map(([provider, cost]) => ({
        provider,
        cost: cost as number,
        percentage: ((cost as number) / totalCost) * 100,
      }));

      // Fetch real monthly trend data from Cost API (already fetched in parallel above)
      console.log('📊 Monthly trend data already fetched in parallel');
      
      // The real ML forecast endpoint (/api/v1/forecasts) has a genuine
      // schema mismatch server-side and has never populated any real
      // forecast data - calling it just adds a wasted round trip and error
      // noise on every single dashboard load. Skipping it entirely and
      // going straight to the simple estimate below (kept honest by the
      // "(estimated)" label applied to the card that uses it) until a real
      // forecast-generation pipeline is built.
      const forecastData: {
        p10?: number;
        p50?: number;
        p90?: number;
        confidence?: number;
        pattern?: 'linear' | 'exponential' | 'seasonal' | 'stable';
      } = {};

      // Commitment coverage (RI/Savings Plan/CUD) - AWS/GCP are mock-derived
      // server-side (no real commitment data source integrated yet), Azure
      // stays dataAvailable:false until a real Reservation Utilization API
      // integration is built.
      let commitmentMetricsData: any = {
        coverage: { coveragePercentage: 0, coveredSpend: 0, uncoveredSpend: 0 },
        utilization: { utilizationPercentage: 0, usedAmount: 0, committedAmount: 0, wasteAmount: 0 },
        commitmentsByType: [],
        providerBreakdown: {
          aws: { commitmentType: 'Savings Plans', coveragePercentage: 0, utilizationPercentage: 0, dataAvailable: false },
          azure: { commitmentType: 'Reservations', coveragePercentage: 0, utilizationPercentage: 0, dataAvailable: false },
          gcp: { commitmentType: 'Committed Use Discounts', coveragePercentage: 0, utilizationPercentage: 0, dataAvailable: false },
        },
      };
      try {
        const commitmentRes = await axios.get(`${API_BASE_URL}/cost/commitment-coverage`, {
          params: { start: startISO, end: endISO },
          headers: authHeaders(),
          timeout: 10000,
        });
        if (commitmentRes.data?.data) {
          commitmentMetricsData = commitmentRes.data.data;
          console.log('✅ Commitment coverage loaded:', commitmentMetricsData.coverage);
        }
      } catch (e: any) {
        console.warn('⚠️ Failed to fetch commitment coverage, showing empty state:', e.message);
      }

      // Budget already fetched at the beginning of this function

      // Fetch REAL previous month cost from Cost API
      // MoM LOGIC: Compare current month (1st → today) vs same days in previous month
      // Example: June 1-17 vs May 1-17 (fair comparison, same number of days)
      let previousCost = 0;
      let currentMonthCost = 0;
      let isPreviousCostEstimated = false;
      /**
       * Month-to-date spend per account, taken from the same MTD response as
       * `currentMonthCost` rather than a second round trip. This is what lets
       * the budget comparison below narrow spend to the budgeted accounts.
       */
      let currentMonthByAccount: Record<string, number> = {};
      try {
        const now = new Date();
        const dayOfMonth = now.getDate();
        
        // Current month: 1st of this month → today
        const currMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const currMonthEnd = new Date(now.getFullYear(), now.getMonth(), dayOfMonth + 1); // +1 to include today
        const currStartISO = `${currMonthStart.toISOString().split('T')[0]}T00:00:00Z`;
        const currEndISO = `${currMonthEnd.toISOString().split('T')[0]}T00:00:00Z`;

        // Previous month: same number of days (1st → same day of prev month)
        const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const prevMonthEnd = new Date(now.getFullYear(), now.getMonth() - 1, dayOfMonth + 1);
        const prevStartISO = `${prevMonthStart.toISOString().split('T')[0]}T00:00:00Z`;
        const prevEndISO = `${prevMonthEnd.toISOString().split('T')[0]}T00:00:00Z`;

        console.log('📊 MoM comparison:', { 
          currentPeriod: `${currStartISO} → ${currEndISO}`,
          previousPeriod: `${prevStartISO} → ${prevEndISO}`,
          daysCompared: dayOfMonth
        });

        // Fetch current month cost
        const currRes = await axios.get(`${API_BASE_URL}/cost/summary`, {
          params: { start: currStartISO, end: currEndISO, ...(provider && { provider }) },
          headers: authHeaders(),
          timeout: 90000,
        });
        if (currRes.data?.data?.total_cost && currRes.data.data.total_cost > 0) {
          currentMonthCost = currRes.data.data.total_cost;
          currentMonthByAccount = currRes.data.data.breakdown?.by_account || {};
          console.log('✅ Current month cost (1st → today):', currentMonthCost);
        }

        // Fetch previous month cost (same days)
        const prevRes = await axios.get(`${API_BASE_URL}/cost/summary`, {
          params: { start: prevStartISO, end: prevEndISO, ...(provider && { provider }) },
          headers: authHeaders(),
          timeout: 90000,
        });
        if (prevRes.data?.data?.total_cost && prevRes.data.data.total_cost > 0) {
          previousCost = prevRes.data.data.total_cost;
          console.log('✅ Previous month cost (same days):', previousCost);
        } else {
          console.warn('Previous month returned 0, using estimate');
          previousCost = currentMonthCost * 0.95;
          isPreviousCostEstimated = true;
        }
      } catch (e: any) {
        console.warn('Failed to fetch MoM costs, using estimate:', e.message);
        currentMonthCost = totalCost;
        previousCost = totalCost * 0.95;
        isPreviousCostEstimated = true;
      }

    // Fetch REAL savings data from optimization-api recommendations
      // No hardcoded fallbacks - show empty state if API is unavailable
      let realTotalSavings: number | null = null;
      let realSavingsPercentage: number | null = null;
      let savingsByCategory: { category: string; amount: number; percentage: number }[] = [];
      let optimizationDataAvailable = false;

      try {
        const OPTIMIZATION_API_URL = import.meta.env.VITE_OPTIMIZATION_API_URL || 'http://localhost:3005';
        const recRes = await axios.get(`${OPTIMIZATION_API_URL}/api/v1/recommendations/summary`, {
          headers: authHeaders(),
          timeout: 30000,
        });

        const recData = recRes.data?.data;
        if (recData && recData.total_savings > 0) {
          realTotalSavings = recData.total_savings;
          realSavingsPercentage = totalCost > 0 ? Math.round((realTotalSavings! / totalCost) * 1000) / 10 : 0;
          optimizationDataAvailable = true;

          // Build category breakdown from real optimization API data
          // Percentages calculated from actual by_category savings amounts
          const byCategory = recData.by_category || {};
          const categoryEntries = Object.entries(byCategory);
          if (categoryEntries.length > 0) {
            savingsByCategory = categoryEntries.map(([category, data]: [string, any]) => ({
              category: category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
              amount: data.savings || 0,
              // Actual percentage: category savings relative to total savings from API
              percentage: realTotalSavings && realTotalSavings > 0
                ? Math.round(((data.savings || 0) / realTotalSavings) * 1000) / 10
                : 0,
            }));
          }
          console.log('✅ Real savings data from optimization-api:', realTotalSavings);
        } else {
          console.log('ℹ️ No savings data available from optimization API');
        }
      } catch (e: any) {
        console.warn('⚠️ Optimization API unavailable - waste index will show empty state:', e.message);
      }

      // Calculate waste index ONLY from real optimization API data
      // No hardcoded fallback multipliers or percentages - show empty state if data unavailable
      let wasteScore: number | null = null;
      let totalWaste: number | null = null;
      let wastePercentage: number | null = null;
      let wasteByCategory: any[] = [];

      if (optimizationDataAvailable && realTotalSavings !== null && realTotalSavings > 0) {
        totalWaste = realTotalSavings;
        wastePercentage = totalCost > 0 ? Math.round((totalWaste / totalCost) * 1000) / 10 : 0;
        wasteScore = Math.min(100, Math.round(wastePercentage * 10) / 10);
        
        // Calculate wasteByCategory percentages directly from optimization API by_category data
        // Percentages are calculated as (category_savings / totalWaste) * 100
        // No hardcoded fallback percentages - if no category data, wasteByCategory stays empty
        if (savingsByCategory.length > 0) {
          const catDesc: Record<string, string> = {
            'Idle Resources': 'Resources with <5% utilization',
            'Idle Resource': 'Resources with <5% utilization',
            'Rightsizing': 'Resources with <40% utilization',
            'Orphaned Resources': 'Unattached volumes, snapshots',
            'Orphaned Resource': 'Unattached volumes, snapshots',
            'Old Snapshots': 'Old snapshots costing storage',
            'Snapshot Cleanup': 'Old snapshots costing storage',
          };
          wasteByCategory = savingsByCategory.map(cat => ({
            category: cat.category === 'Rightsizing' ? 'Overprovisioned' : cat.category,
            amount: cat.amount,
            // Calculate actual percentage from optimization API data (category amount / total waste)
            percentage: totalWaste && totalWaste > 0
              ? Math.round((cat.amount / totalWaste) * 1000) / 10
              : 0,
            description: catDesc[cat.category] || 'Optimization opportunity',
          }));
        }
      }

      const wasteIndexData = {
        wasteScore,
        totalWaste,
        wastePercentage,
        wasteByCategory,
        dataAvailable: optimizationDataAvailable,
      };

      // Hoisted out of the returned object literal so both the anomaly list
      // and the hidden-by-default-filter count can be read from one fetch.
      const anomalyResult = await this.fetchTopAnomalies(provider);

    // Build spendVsBudget with visual indicators
      // REQ-001 AC-001.2: No budget configured → return configured: false
      // CURRENCY GUARD: Compare against the budget's own currency, not a hardcoded
      // assumption of USD — budgets can be configured in USD/INR/EUR/GBP. A budget
      // only applies when its currency matches what's currently being displayed
      // (e.g. an INR budget applies to Azure-only INR views, a USD budget to
      // multi-provider/AWS views). Mismatched currencies are hidden rather than
      // compared incorrectly, since this doesn't do live currency conversion.
      const budgetApplicable = budget !== null && budget > 0 && budgetCurrency === currency;

      // Spend for the BUDGET's period (this month, 1st -> today), which is a
      // different number from the selected viewing window.
      //
      // Variance used to compare `totalCost` - the selected window - against a
      // MONTHLY budget. On "Last 90 Days" that charged a quarter of spend
      // against one month's budget and always looked catastrophically over; on
      // "Last 7 Days" it always looked comfortably under. Neither told the user
      // anything true. A monthly budget can only be judged against
      // month-to-date spend.
      const totalMonthSpend = currentMonthCost || totalCost;

      // COVERAGE. The budget is a sum of the accounts that HAVE a budget, so
      // the spend it is compared against must be the spend of those same
      // accounts - not the tenant's whole bill.
      //
      // Skipping this produced a confirmed-live "2204.30% over pace": two
      // budgeted accounts worth a few hundred dollars were being measured
      // against every account's month-to-date spend. The number was not a
      // little off, it was measuring a different thing.
      //
      // If none of the budgeted account ids appear in the cost breakdown - a
      // budget set on an account that has not reported spend yet, or an id
      // written in a form the cost export does not use - attributing would
      // yield $0 and read as "100% under pace", which is a confident wrong
      // answer. In that case fall back to total spend and mark coverage
      // unknown so the tile can say the comparison is approximate.
      let budgetPeriodSpend = totalMonthSpend;
      let budgetedAccountsMatched = 0;
      let unbudgetedSpend = 0;
      let coverageKnown = false;

      if (budgetedAccountIds.size > 0 && Object.keys(currentMonthByAccount).length > 0) {
        let matchedSpend = 0;
        for (const [accountId, cost] of Object.entries(currentMonthByAccount)) {
          if (budgetedAccountIds.has(String(accountId).toLowerCase())) {
            matchedSpend += Number(cost) || 0;
            budgetedAccountsMatched += 1;
          } else {
            unbudgetedSpend += Number(cost) || 0;
          }
        }

        if (budgetedAccountsMatched > 0) {
          budgetPeriodSpend = matchedSpend;
          coverageKnown = true;
        } else {
          unbudgetedSpend = 0;
          console.warn(
            '⚠️ Budgeted accounts do not match any account in the cost breakdown - ' +
              'pace is measured against TOTAL spend and is approximate',
            {
              budgeted: Array.from(budgetedAccountIds),
              inSpend: Object.keys(currentMonthByAccount),
            }
          );
        }
      }

      // PACING. Comparing month-to-date spend against a FULL month's budget was
      // still wrong, just less obviously so than comparing a 90-day window.
      // On the 16th of a 30-day month, spending exactly on plan shows as "47%
      // under budget" - so the tile reads reassuring for three weeks and only
      // turns red once it is too late to act. Measured live during this build:
      // $199,179 MTD against a $330,000 budget rendered as "39.6% under" while
      // the run rate was actually 13% OVER.
      //
      // Two honest numbers instead, which is what AWS Budgets, Azure Cost
      // Management and CloudHealth all show:
      //   pace     - MTD vs the budget pro-rated to days elapsed ("on track today?")
      //   forecast - projected month-end vs the full budget ("will we blow it?")
      const now = new Date();
      const daysElapsed = now.getDate();
      const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
      const monthProgress = daysElapsed / daysInMonth;

      const spendVsBudget: any = {
        // Stays the SELECTED window - this drives the "Total Spend" tile, which
        // must keep following the period selector.
        currentSpend: totalCost,
        // Month-to-date, what the variance below is actually computed from.
        // NOTE: this is the MTD spend of the BUDGETED accounts only when
        // coverage is known - deliberately not the tenant total, see above.
        budgetPeriodSpend,
        budgetPeriod,
        // Month-to-date spend across every account in view, budgeted or not.
        // Kept separate so the card can show both without one being mistaken
        // for the other.
        totalMonthSpend,
        // Budget coverage, so the tile can say what the pace does NOT include.
        budgetedAccounts: budgetedAccountIds.size,
        budgetedAccountsWithSpend: budgetedAccountsMatched,
        unbudgetedSpend: Math.round(unbudgetedSpend * 100) / 100,
        budgetCoverageKnown: coverageKnown,
        budgetAmount: budgetApplicable ? budget : null,
        budgetConfigured: budgetApplicable ? budgetConfigured : false,
        daysElapsed,
        daysInMonth,
        currency: currency,
      };

      if (budgetApplicable) {
        // budget is guaranteed non-null here (budgetApplicable checks budget !== null && budget > 0)
        const budgetValue = budget as number;

        // Budget available so far this month, if spend were perfectly even.
        const proRatedBudget = budgetValue * monthProgress;

        // Straight-line projection of MTD to month-end. Deliberately simple and
        // explainable: a viewer can verify it in their head, which matters more
        // here than sophistication - and it degrades safely on day 1.
        const projectedMonthEnd = monthProgress > 0 ? budgetPeriodSpend / monthProgress : budgetPeriodSpend;

        // Variance is now PACE-based: MTD against the pro-rated budget.
        const variance = budgetPeriodSpend - proRatedBudget;
        const variancePercentage =
          proRatedBudget > 0
            ? Math.round(((budgetPeriodSpend - proRatedBudget) / proRatedBudget) * 10000) / 100
            : 0;

        // Visual indicator logic
        let varianceStatus: 'safe' | 'warning' | 'danger' | 'critical';
        if (variancePercentage < -25) {
          varianceStatus = 'safe'; // Well under pace
        } else if (variancePercentage < 0) {
          varianceStatus = 'warning'; // Under pace but close
        } else if (variancePercentage < 10) {
          varianceStatus = 'danger'; // Over pace
        } else {
          varianceStatus = 'critical'; // Significantly over pace
        }

        spendVsBudget.proRatedBudget = Math.round(proRatedBudget * 100) / 100;
        spendVsBudget.projectedMonthEnd = Math.round(projectedMonthEnd * 100) / 100;
        spendVsBudget.projectedOverage = Math.round((projectedMonthEnd - budgetValue) * 100) / 100;
        spendVsBudget.variance = variance;
        spendVsBudget.variancePercentage = variancePercentage;
        spendVsBudget.varianceStatus = varianceStatus;
        // Use ML-based forecast (p50) if available, otherwise fallback to simple calculation
        spendVsBudget.forecastedSpend = forecastData.p50 || totalCost * 1.02;
      } else {
        // No budget configured - indicate setup needed
        spendVsBudget.variance = null;
        spendVsBudget.variancePercentage = null;
        spendVsBudget.varianceStatus = null;
        // Use ML-based forecast (p50) if available, otherwise fallback to simple calculation
        spendVsBudget.forecastedSpend = forecastData.p50 || totalCost * 1.02;
      }

    return {
      // Non-fatal API warnings (e.g. a provider whose credentials could not be
      // read). Empty in the normal case; the Dashboard shows a banner when not.
      dataWarnings,
      spendVsBudget,
      momTrend: {
        currentMonth: currentMonthCost || totalCost,
        previousMonth: previousCost,
        change: (currentMonthCost || totalCost) - previousCost,
        changePercentage: previousCost > 0 
          ? Math.round((((currentMonthCost || totalCost) - previousCost) / previousCost) * 10000) / 100
          : 0,
        trend: (currentMonthCost || totalCost) > previousCost ? 'up' : 'down',
        monthlyData,
        isPreviousMonthEstimated: isPreviousCostEstimated,
      },
      realizedSavings: {
        totalSavings: realTotalSavings ?? 0,
        savingsPercentage: realSavingsPercentage ?? 0,
        savingsByCategory,
        // No real month-by-month savings history is tracked anywhere yet -
        // this used to fill the gap with random numbers whenever real
        // current-month savings existed, which is worse than showing
        // nothing. Empty until a real history is actually tracked.
        monthlyTrend: [],
      },
      forecastAccuracy: {
        // Accuracy = how close forecast was to actual current month spend
        // If ML forecast (p50) available: compare p50 vs actual current month
        // If no ML forecast: compare simple estimate (prev * 1.05) vs actual
        accuracyPercentage: (() => {
          const actual = currentMonthCost || totalCost;
          const forecast = forecastData.p50 || (previousCost > 0 ? previousCost * 1.05 : actual);
          if (actual > 0 && forecast > 0) {
            return Math.round((1 - Math.abs(actual - forecast) / actual) * 1000) / 10;
          }
          return 0;
        })(),
        forecastedAmount: forecastData.p50 || (previousCost > 0 ? previousCost * 1.05 : (currentMonthCost || totalCost)),
        actualAmount: currentMonthCost || totalCost,
        variance: forecastData.p50 
          ? Math.abs((currentMonthCost || totalCost) - forecastData.p50)
          : (previousCost > 0 ? Math.abs((currentMonthCost || totalCost) - previousCost * 1.05) : 0),
        lastUpdated: new Date().toISOString(),
        // REQ-002 AC-002.3: Display "Projected: $X (range: $Y-$Z)" with confidence indicator
        p10: forecastData.p10,
        p50: forecastData.p50,
        p90: forecastData.p90,
        confidence: forecastData.confidence,
        pattern: forecastData.pattern,
      },
      // Commitment metrics - REQ-003. AWS/GCP now mock-derived server-side
      // (see cost.service.ts queryCommitmentCoverage); Azure stays
      // "Coming Soon" until a real Reservation Utilization API is wired in.
      commitmentMetrics: commitmentMetricsData,
      wasteIndex: wasteIndexData,
      // Task 18: Fetch real anomalies from API (top 5 by delta_amount)
      // REQ-004 AC-004.3: Storage & Display
      topAnomalies: anomalyResult.anomalies,
      hiddenAnomalyCount: anomalyResult.hiddenCount,
      costByRegion: regionData,
      costByService: serviceData,
      costByAccount: accountData,
      costByProvider: providerData,
      currency: currency, // Pass through from API response (USD for multi-provider, INR for Azure-only)
      lastUpdated: new Date().toISOString(),
    };
    } catch (error: any) {
      console.error('❌ Failed to fetch cost data from S3:', error.message);
      throw new Error(`Dashboard data unavailable: ${error.message}`);
    }
  }

  /**
   * Fetch per-provider summary data from the cost API
   * REQ-012-HYBRID: Returns topServices, wastePercentage, savingsOpportunity, momTrend per provider
   * 
   * Tries the dedicated /cost/provider-summary endpoint first, then falls back to
   * the by_provider field in /cost/summary if available.
   */
  async getProviderSummary(params: DashboardQueryParams): Promise<ProviderSummaryData | null> {
    const { startDate, endDate } = this.getDateRangeForParams(params.timePeriod);
    const startISO = `${startDate}T00:00:00Z`;
    const endISO = `${endDate}T23:59:59Z`;

    try {
      const response = await axios.get(`${API_BASE_URL}/cost/provider-summary`, {
        params: { start: startISO, end: endISO },
        headers: authHeaders(),
        timeout: 30000,
      });

      const data = response.data?.data;
      if (data && data.providers) {
        return data as ProviderSummaryData;
      }
      return null;
    } catch (error: any) {
      console.warn('⚠️ Failed to fetch provider summary from dedicated endpoint, trying /cost/summary fallback:', error.message);

      // Fallback: Try to extract from /cost/summary by_provider field (REQ-012-HYBRID AC-012.7)
      try {
        const summaryResponse = await axios.get(`${API_BASE_URL}/cost/summary`, {
          params: { start: startISO, end: endISO },
          headers: authHeaders(),
          timeout: 30000,
        });

        const summaryData = summaryResponse.data?.data;
        if (summaryData?.by_provider && Object.keys(summaryData.by_provider).length > 0) {
          console.log('✅ Using by_provider field from /cost/summary as fallback');
          return {
            total_cost: summaryData.total_cost,
            currency: summaryData.currency || 'USD',
            period_start: summaryData.period_start,
            period_end: summaryData.period_end,
            providers: summaryData.by_provider,
          } as ProviderSummaryData;
        }
      } catch (fallbackError: any) {
        console.warn('⚠️ Fallback /cost/summary also failed:', fallbackError.message);
      }

      return null;
    }
  }

  /**
   * Helper method to get date range for API calls
   */
  private getDateRangeForParams(timePeriod: TimePeriod): { startDate: string; endDate: string } {
    const endDate = new Date();
    const startDate = new Date();

    switch (timePeriod) {
      case TimePeriod.LAST_7_DAYS:
        startDate.setDate(endDate.getDate() - 7);
        break;
      case TimePeriod.LAST_30_DAYS:
        startDate.setDate(endDate.getDate() - 30);
        break;
      case TimePeriod.LAST_90_DAYS:
        startDate.setDate(endDate.getDate() - 90);
        break;
      case TimePeriod.LAST_12_MONTHS:
        startDate.setMonth(endDate.getMonth() - 12);
        break;
      case TimePeriod.LAST_24_MONTHS:
        startDate.setMonth(endDate.getMonth() - 24);
        break;
      default:
        startDate.setDate(endDate.getDate() - 90);
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
    };
  }

  /**
   * Fetch real monthly trend data from Cost API
   */
  private async fetchMonthlyTrendData(
    startDate: string,
    endDate: string,
    timePeriod: TimePeriod,
    budgetAmount: number | null = null,
    provider?: string,
    /**
     * Each month's own budget, keyed 'YYYY-MM'. Falls back to `budgetAmount`
     * for any bucket with no budget recorded for its month, which keeps the
     * line continuous rather than dropping out mid-chart.
     */
    budgetByPeriod: Record<string, number> = {}
  ): Promise<{ month: string; spend: number; budget?: number; by_provider?: Record<string, number> }[]> {
    try {
      // Fetch daily time series data
      const response = await axios.get(`${API_BASE_URL}/cost/timeseries`, {
        params: {
          start: startDate,
          end: endDate,
          granularity: 'daily',
          currency: 'USD',
          ...(provider && { provider }),
        },
        headers: authHeaders(),
        timeout: 90000,
      });

      const dailyData = response.data.data || [];

      // Bucket to match the SELECTED period.
      //
      // `timePeriod` was accepted here and then never read: every window was
      // aggregated into calendar months regardless. Picking "Last 7 Days"
      // fetched 7 days correctly and then collapsed all of them into a single
      // bar labelled with the month - so the chart looked identical for 7, 30
      // and 90 days, and a "trend" chart showed no trend at all.
      //
      // Short windows are bucketed DAILY (7 bars for a week), medium windows
      // WEEKLY (keeps ~13 bars for a quarter rather than 90), and only the
      // 12/24-month views stay monthly - which is the only scale where a
      // calendar month is the meaningful unit.
      const bucketBy: 'day' | 'week' | 'month' =
        timePeriod === TimePeriod.LAST_7_DAYS || timePeriod === TimePeriod.LAST_30_DAYS
          ? 'day'
          : timePeriod === TimePeriod.LAST_90_DAYS
          ? 'week'
          : 'month';

      const bucketLabel = (d: Date): string => {
        if (bucketBy === 'month') return d.toLocaleString('default', { month: 'short', year: 'numeric' });
        if (bucketBy === 'day') return d.toLocaleString('default', { month: 'short', day: 'numeric' });
        // Week: label by the week's Monday, so every day in that week maps to
        // one stable key regardless of the order points arrive in.
        const monday = new Date(d);
        const dow = (monday.getDay() + 6) % 7; // Mon=0 ... Sun=6
        monday.setDate(monday.getDate() - dow);
        return monday.toLocaleString('default', { month: 'short', day: 'numeric' });
      };

      // Keeps each bucket's real by_provider split (rather than replaying one
      // whole-period ratio across every bar) so SpendChart stacks each bar
      // with the provider mix that bucket actually had.
      const monthlyMap = new Map<string, { total: number; byProvider: Record<string, number>; sortKey: number }>();

      for (const point of dailyData) {
        const date = new Date(point.timestamp);
        const monthKey = bucketLabel(date);
        const entry = monthlyMap.get(monthKey) || { total: 0, byProvider: {}, sortKey: date.getTime() };
        // Earliest timestamp in the bucket orders it - a label like "Sep 8"
        // is not reliably parseable by `new Date()` across locales, which is
        // what the old string-parsing sort depended on.
        entry.sortKey = Math.min(entry.sortKey, date.getTime());
        entry.total += point.cost;
        for (const [providerKey, providerCost] of Object.entries(point.by_provider || {})) {
          entry.byProvider[providerKey] = (entry.byProvider[providerKey] || 0) + Number(providerCost || 0);
        }
        monthlyMap.set(monthKey, entry);
      }

      // SCALE THE BUDGET TO THE BUCKET.
      //
      // The budget is a MONTHLY figure. Attaching it unscaled to every bucket
      // drew a $330,000 line over daily bars worth ~$11,000 each — and because
      // the chart's y-axis maximum is max(spend, budget), that line also set the
      // scale, squashing every bar to a sliver at the bottom. The chart became
      // unreadable the moment a budget was configured, which is exactly when a
      // user most wants to look at it.
      //
      // A daily bar belongs against a daily budget. 30.44 is the mean month
      // length, so the weekly and daily figures stay stable across months
      // instead of jumping between 28- and 31-day months.
      const AVG_DAYS_PER_MONTH = 30.44;
      const now = new Date();
      const MS_PER_DAY = 24 * 60 * 60 * 1000;

      /**
       * How much of a bucket has actually happened, 0–1.
       *
       * Without this the newest bar is judged against a whole period's budget
       * while showing only part of that period's spend, so it always looks
       * safe. Worked example: budget $30,000/month, spending $1,150/day, today
       * the 25th. The bar shows 25 x $1,150 = $28,750 against a $30,000 line
       * and reads as under budget - while the run rate lands the month at
       * $34,500, or $4,500 over. Pro-rating the line to 25/30 x $30,000 =
       * $25,000 puts the bar above it, which is the truth.
       *
       * Daily buckets are left alone: AWS CUR and Azure exports land 6-24h
       * behind usage, so a partial-day figure is a data-lag artefact rather
       * than a real underspend, and scaling the line by hours would invent
       * precision the data does not have.
       */
      const bucketCompleteness = (bucketStartMs: number): number => {
        if (bucketBy === 'month') {
          const start = new Date(bucketStartMs);
          const isCurrentMonth =
            start.getFullYear() === now.getFullYear() && start.getMonth() === now.getMonth();
          if (!isCurrentMonth) return 1;
          const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
          return Math.min(1, now.getDate() / daysInMonth);
        }
        if (bucketBy === 'week') {
          // Re-derive the bucket's Monday the same way bucketLabel does, so a
          // week whose first data point is a Wednesday is still measured from
          // its Monday and not from that Wednesday.
          const monday = new Date(bucketStartMs);
          monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
          monday.setHours(0, 0, 0, 0);
          const elapsedDays = Math.floor((now.getTime() - monday.getTime()) / MS_PER_DAY) + 1;
          if (elapsedDays >= 7) return 1;
          return Math.max(0, Math.min(1, elapsedDays / 7));
        }
        return 1;
      };

      /** The monthly budget that applies to the month a bucket falls in. */
      const monthlyBudgetFor = (bucketStartMs: number): number | null => {
        const d = new Date(bucketStartMs);
        // A weekly bucket can straddle two months; it is attributed to the
        // month its Monday falls in, matching how the bucket is labelled.
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        const forThatMonth = budgetByPeriod[key];
        if (forThatMonth && forThatMonth > 0) return forThatMonth;
        return budgetAmount && budgetAmount > 0 ? budgetAmount : null;
      };

      const budgetForBucket = (bucketStartMs: number): number | undefined => {
        const monthly = monthlyBudgetFor(bucketStartMs);
        if (!monthly) return undefined;
        const full =
          bucketBy === 'month'
            ? monthly
            : bucketBy === 'week'
            ? (monthly / AVG_DAYS_PER_MONTH) * 7
            : monthly / AVG_DAYS_PER_MONTH;
        return Math.round(full * bucketCompleteness(bucketStartMs));
      };

      // Convert to array format
      const monthlyData = Array.from(monthlyMap.entries())
        .map(([month, { total, byProvider, sortKey }]) => ({
          month,
          spend: Math.round(total),
          budget: budgetForBucket(sortKey),
          by_provider: byProvider,
          sortKey,
        }))
        // Sort on the real timestamp captured above, not on the display label.
        // The previous `new Date(a.month)` worked only because every label was
        // a parseable "Sep 2026"; day/week labels like "Sep 8" parse
        // inconsistently across locales and would have shuffled the bars.
        .sort((a, b) => a.sortKey - b.sortKey)
        .map(({ sortKey, ...rest }) => rest);

      console.log('✅ Spend trend aggregated', { buckets: monthlyData.length, bucketBy, timePeriod });
      return monthlyData;

    } catch (error: any) {
      console.error('❌ Failed to fetch monthly trend data:', error.message);
      // Fallback to generated data if API fails
      return this.generateMonthlyData(timePeriod, budgetAmount);
    }
  }

  /**
   * Generate monthly data points based on time period
   */
  private generateMonthlyData(timePeriod: TimePeriod, budgetAmount: number | null = null) {
    const now = new Date();
    const months: { month: string; spend: number; budget?: number }[] = [];

    let numMonths = 3;
    if (timePeriod === TimePeriod.LAST_90_DAYS) numMonths = 3;
    else if (timePeriod === TimePeriod.LAST_12_MONTHS) numMonths = 12;
    else if (timePeriod === TimePeriod.LAST_24_MONTHS) numMonths = 24;

    for (let i = numMonths - 1; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleString('default', { month: 'short', year: 'numeric' });
      const baseSpend = 450000 + Math.random() * 50000;
      months.push({
        month: monthName,
        spend: Math.round(baseSpend),
        budget: budgetAmount && budgetAmount > 0 ? budgetAmount : undefined,
      });
    }

    return months;
  }

  /**
   * Fetch top anomalies from optimization-api - the SAME engine the standalone
   * Anomalies page uses (previously this called a separate, older anomaly
   * engine in cost-api, so the Dashboard and the Anomalies page could show
   * inconsistent results - now both read from one source of truth).
   * Task 18: Display top 5 anomalies by delta_amount
   * REQ-004 AC-004.3: Storage & Display
   */
  private async fetchTopAnomalies(provider?: string): Promise<{ anomalies: any[]; hiddenCount: number }> {
    try {
      const OPTIMIZATION_API_URL = import.meta.env.VITE_OPTIMIZATION_API_URL || 'http://localhost:3005';
      const response = await axios.get(`${OPTIMIZATION_API_URL}/api/v1/anomalies`, {
        params: { ...(provider && { provider }) },
        headers: authHeaders(),
        timeout: 10000,
      });

      const anomalies = response.data?.data?.anomalies || [];
      // The API deliberately filters out informational `new_service` findings
      // by default and reports how many it hid - carry that through so the
      // widget can say so instead of a bare "No anomalies detected".
      const hiddenCount = response.data?.data?.hidden_by_default_filter || 0;
      const statusMap: Record<string, string> = {
        open: 'open',
        acknowledged: 'investigating',
        resolved: 'resolved',
        false_positive: 'dismissed',
      };

      // Map optimization-api's anomaly format to the dashboard's TopAnomaly shape
      const mappedAnomalies = anomalies
        .slice(0, 5)
        .map((anom: any) => ({
          id: anom.id,
          title: anom.message || `Cost anomaly in ${anom.service || 'service'}`,
          severity: anom.severity,
          detectedAt: anom.detected_at,
          scope: anom.service ? `Service: ${anom.service}` : 'Unknown scope',
          deltaAmount: (anom.current_cost || 0) - (anom.normal_cost || 0),
          deltaPercentage: anom.increase_percent || 0,
          status: statusMap[anom.status] || 'open',
          provider: anom.provider || undefined,
        }));

      console.log('✅ Anomalies loaded from optimization-api:', mappedAnomalies.length, 'hidden:', hiddenCount);
      return { anomalies: mappedAnomalies, hiddenCount };
    } catch (error: any) {
      console.warn('⚠️ Failed to fetch anomalies, returning empty array:', error.message);
      return { anomalies: [], hiddenCount: 0 }; // Honest-empty instead of mock data
    }
  }

}

export const dashboardService = new DashboardService();
